import { useLayoutEffect, useRef, useState } from 'react'
import type { AgentLog } from '../../types'
import { computeAnchorAction } from '../../lib/agentLogFormat'
import { LogLine } from './LogLine'

interface Props {
  /** Chronological order — oldest first, newest last (console style). */
  logs: AgentLog[]
  hasOlder: boolean
  loadingOlder: boolean
  onLoadOlder: () => void
}

/**
 * Dark monospace console. Auto-scrolls to the bottom (newest) while the user is
 * stuck there; if they scroll up to read, new lines stop yanking the view.
 * "Load older" pulls the next keyset page (older logs) onto the top.
 *
 * MVP renders all lines (memo'd). Real virtualization is a follow-up if a phone
 * with days of logs makes the DOM heavy — there is no react-window in deps yet.
 */
export function LogConsole({ logs, hasOlder, loadingOlder, onLoadOlder }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [stuck, setStuck] = useState(true)
  // Anchored to the top row's id + its viewport offset at the "Load older"
  // click. Pinning a specific element (not raw scrollHeight) and gating the
  // restore on the top row actually changing means a live-poll bottom-append
  // that resolves before the older page can't consume the anchor and mis-shift
  // the view — it waits for the real prepend.
  const prependAnchor = useRef<{ id: string; top: number } | null>(null)

  function offsetWithin(el: HTMLDivElement, id: string): number | null {
    const node = el.querySelector<HTMLElement>(`[data-log-id="${CSS.escape(id)}"]`)
    return node ? node.getBoundingClientRect().top - el.getBoundingClientRect().top : null
  }

  // useLayoutEffect (not useEffect): the scroll write must happen synchronously
  // after the DOM mutates and BEFORE paint, or the user sees a flash/jump.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const anchor = prependAnchor.current
    if (anchor) {
      const action = computeAnchorAction(logs[0]?.id, anchor.id, loadingOlder)
      if (action === 'restore') {
        // The top row changed → older rows actually prepended. Restore the
        // anchored row to its prior viewport offset (bottom-appends don't move
        // it, so this is exact). jsdom has no layout → offsetWithin is null →
        // no-op; the real restore is browser-verified (web-render-verification).
        const now = offsetWithin(el, anchor.id)
        if (now !== null) el.scrollTop += now - anchor.top
        prependAnchor.current = null
      } else if (action === 'clear') {
        // The "Load older" fetch resolved with nothing on top (empty page, or
        // every new row filtered out by search). Release the anchor so the
        // bottom-stick auto-scroll resumes — otherwise it sticks forever and the
        // console silently stops following new lines until remount.
        prependAnchor.current = null
      }
      // 'restore'/'clear' handled, or 'wait' (fetch still in flight, a
      // bottom-append slipped in) — never autoscroll while an anchor is pending.
      return
    }
    if (stuck) {
      el.scrollTop = el.scrollHeight
    }
  }, [logs, stuck, loadingOlder])

  function onScroll() {
    const el = ref.current
    if (!el) return
    setStuck(el.scrollHeight - el.scrollTop - el.clientHeight < 40)
  }

  function handleLoadOlder() {
    // Capture the current top row + its viewport offset so the layout effect can
    // restore exactly that row once the older page renders above it.
    const el = ref.current
    const firstId = logs[0]?.id
    prependAnchor.current =
      el && firstId ? { id: firstId, top: offsetWithin(el, firstId) ?? 0 } : null
    onLoadOlder()
  }

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className="flex-1 overflow-auto rounded-md bg-gray-950 p-3 font-mono text-xs leading-relaxed"
    >
      {hasOlder && (
        <button
          type="button"
          onClick={handleLoadOlder}
          disabled={loadingOlder}
          className="mb-2 w-full rounded border border-gray-700 py-1 text-gray-400 hover:bg-gray-800 disabled:opacity-50"
        >
          {loadingOlder ? 'Loading…' : 'Load older'}
        </button>
      )}
      {logs.length === 0 ? (
        <div className="text-gray-600">No logs.</div>
      ) : (
        logs.map((log) => <LogLine key={log.id} log={log} />)
      )}
    </div>
  )
}
